import {
    Card, CardActionArea, CardContent, Checkbox,
    Grid,
    Typography, Button, CircularProgress, Backdrop
} from "@mui/material";
import {useForm, FormProvider} from "react-hook-form";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Box} from "@mui/system";
import moment from "moment";
import {CompanyFormCard} from "~/apps/packages/passes/components/PassForm/CompanyFormCard";
import {PersonalFormCard} from "~/apps/packages/passes/components/PassForm/PersonalFormCard";
import {CarFormCard} from "~/apps/packages/passes/components/PassForm/CarFormCard";
import {AdmissionFormCard} from "~/apps/packages/passes/components/PassForm/AdmissionFormCard";
import {AgreementFormCard} from "~/apps/packages/passes/components/PassForm/AgreementFormCard";
import {PassportFormCard} from "~/apps/packages/passes/components/PassForm/PassportFormCard";
import {UploadFormCard} from "~/apps/packages/passes/components/PassForm/UploadFormCard";
import {DocumentsCard} from "~/apps/packages/passes/components/PassForm/DocumentsCard";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {ConfirmActionDialog} from "~/components/ConfirmActionDialog";
import {useHistory, useLocation} from "react-router-dom";
import {makeStyles} from "@mui/styles";
import {PortTheme} from "~/ui/theme";
import {LoaderLight} from "~/ui/icons";
import {PopupMessage} from "~/components/PopupMessage";

const useStyles = makeStyles<PortTheme>((theme) => ({
    loader: {
        width: 24,
        height: 24,
        animation: "$spin 1s linear infinite",
    },
    "@keyframes spin": {
        "0%": {
            transform: "rotate(0deg)",
        },
        "100%": {
            transform: "rotate(360deg)",
        },
    },
}))

export type Values = {
    companyName: string
    inn: string
    chiefFullName: string
    chiefPhone: string

    fullName: string
    position: string
    phone: string
    email: string
    birthDate: Date
    birthPlace: string
    passportSeries: string
    passportNumber: string
    passportDate: Date
    passportIssuer: string
    registrationAddress: string
    actualAddress: string

    carRegNumber?: string
    carBrand?: string
    carModel?: string
    carColor?: string
    carType?: number

    passDate: Date
    companyActivityKind: string
    paragraph: string
    accessZone1: boolean
    accessZone2: boolean
    accessZone3: boolean

    agreement: boolean

    documents: File[]
}
const initialValues: Values = {
    companyName: "",
    inn: "",
    chiefFullName: "",
    chiefPhone: "",

    fullName: "",
    position: "",
    phone: "",
    email: "",
    birthDate: null,
    birthPlace: "",
    passportSeries: "",
    passportNumber: "",
    passportDate: null,
    passportIssuer: "",
    registrationAddress: "",
    actualAddress: "",

    carRegNumber: "",
    carBrand: "",
    carModel: "",
    carColor: "",
    carType: null,

    passDate: null,
    companyActivityKind: "",
    paragraph: "",
    accessZone1: true,
    accessZone2: false,
    accessZone3: false,

    agreement: false,
    documents: [],
}

export type Type = "PEDESTRIAN" | "CAR";
type Step = "type" | "company" | "employee" | "car" | "admission" | "upload";


export function serializeValues(values: Values): {[key: string]: any} {
    values = {...values}
    for (const key of ["birthDate", "passportDate", "passDate"]) {
        values[key] = values[key] instanceof moment ? values[key].valueOf() : null;
    }
    for (const key of ["phone", "chiefPhone"]) {
        values[key] = values[key] ? values[key].substring(values[key].length - 10, 11) : "";
    }
    return {
        ...values,
        birthDate: values?.birthDate?.valueOf(),
        passportDate: values?.passportDate?.valueOf(),
        passDate: values?.passDate?.valueOf(),
    }
}

export function deserializeValues(values: {[key: string]: any}): Values {
    values = {...values}
    for (const key of ["birthDate", "passportDate", "passDate"]) {
        values[key] = values[key] ? moment(values[key]).toDate() : null;
    }
    return values as Values;
}


export type PassFormProps = {
    step?: number
    defaultValues?: Values
    defaultTypes?: Type[]
    onStepChange?: (step: number, maxSteps: number) => any
    onChange?: (values: Values) => any
    onSubmit?: (values: Values, types: Type[]) => any | Promise<any>
    loading?: boolean
}


function getSteps(types: Type[]): Step[] {
    if (types.includes("CAR")) {
        return ["type", "company", "employee", "car", "admission", "upload"];
    } else if (types.includes("PEDESTRIAN")) {
        return [ "type",  "company", "employee", "admission", "upload"];
    }
    return ["type"];
}


export const PassForm = (props: PassFormProps) => {
    const {onSubmit, onChange, onStepChange, loading, defaultValues: defaultValuesProp, step: stepNumber, defaultTypes: defaultTypesProps} = props;
    const defaultValues: Values = {
        ...initialValues,
        ...defaultValuesProp,
    };
    const classes = useStyles();
    const history = useHistory();
    const locationHook = useLocation();
    const $values = useRef<Values>(defaultValues);
    const [types, setTypes] = useState<Type[]>(defaultTypesProps || []);
    const [isNoDocumentsConfirmOpen, setIsNoDocumentsConfirmOpen] = useState({open: false});

    const [companyDataLoading, setCompanyDataLoading] = useState(false);

    const [isAlertVisible, setAlertVisible] = useState({open: false, notification: null})

    const form = useForm<Values>({
        mode: "onChange",
        defaultValues: defaultValues,
    });
    const {handleSubmit: submitWrapper, formState, trigger, watch, errors} = form;
    const {isValid, isSubmitting} = formState;
    const submitDisabled = isSubmitting || !types.length || loading || companyDataLoading;

    const watchValues = watch();
    const locationSearch = useRef(null);
    const steps = useMemo(() => getSteps(types), [types]);

    const step = useMemo(() => {
        if ((stepNumber || 0) > steps.length) {
            return steps[0];
        }
        return steps[stepNumber || 0];
    }, [stepNumber, steps]);

    // initial validate
    useEffect(() => {
        trigger();
    }, [])

    useEffect(() => {

        for (const [key, value] of Object.entries(watchValues)) {
            if (!value && defaultValues[key]) {
                $values.current[key] = defaultValues[key];
            } else {
                $values.current[key] = value;
            }
        }
        onChange && onChange($values.current);
    }, [watchValues])

    useEffect(() => {
        if (Object.keys(errors)?.length > 0) {
            setAlertVisible({
                open: true,
                notification: {
                    title: 'Заполните форму',
                    message: 'Пропущены обязательные поля'
                }
            })
        }
    }, [errors])

    const handleTypeClick = (value: Type) => {
        const nextTypes = [...types];
        const index = nextTypes.indexOf(value);
        if (index == -1) {
            nextTypes.push(value);
        } else {
            nextTypes.splice(index, 1);
        }
        setTypes(nextTypes);

        const steps = getSteps(nextTypes)
        onStepChange && onStepChange(steps.indexOf(step), steps.length);
    }

    const handleSubmit = submitWrapper((data) => {
        const lastStep = steps[steps.length - 1];
        const documents = data.documents;
        if (step == lastStep) {
            if (documents.length === 0) {
                locationSearch.current = history.location.search;
                setIsNoDocumentsConfirmOpen({open: true});
            } else {
                onSubmit && onSubmit($values.current, types);
            }
        } else {
            onStepChange && onStepChange(steps.indexOf(step) + 1, steps.length);
        }
    });

    const handleCloseConfirmDialog = () => {
        setIsNoDocumentsConfirmOpen({open: false})
        //TODO: Fix this hack
        if (locationSearch.current) {
            history.replace({
                ...locationHook,
                search: locationSearch.current
            });
        }
    }

    const handleConfirmSubmitWithoutDocuments = () => {
        setIsNoDocumentsConfirmOpen({open: false})
        onSubmit && onSubmit($values.current, types);
    }

    return (
        <Box m={2}>
            <ConfirmActionDialog
                open={isNoDocumentsConfirmOpen.open}
                dialogId="confirmDocuments"
                confirmButtonText="Продолжить"
                onClose={handleCloseConfirmDialog}
                onConfirm={handleConfirmSubmitWithoutDocuments}
            >
                <Typography
                    width="100%"
                    align="center"
                    variant="screenText"
                >
                    Заявка без приложенных файлов
                    может быть отклонена
                </Typography>
            </ConfirmActionDialog>
            <form onSubmit={handleSubmit} noValidate>
                <FormProvider {...form}>
                    <Grid container direction="column" rowSpacing={2} wrap="nowrap">
                        {step == "type" && (
                            <>
                                <Grid item>
                                    <Typography variant="screenText" color="primary">
                                        Тип пропуска
                                    </Typography>
                                </Grid>
                                <Grid item container direction="column" rowSpacing="8px" wrap="nowrap">
                                    {["CAR", "PEDESTRIAN"].map((type: Type, index) => (
                                        <Grid item key={index}>
                                            <Card style={{borderRadius: 8, boxShadow: "0px 4px 24px rgba(104, 117, 130, 0.09)"}}>
                                                <CardActionArea onClick={() => handleTypeClick(type)}>
                                                    <CardContent style={{padding: "12px 16px", paddingRight: "8px"}}>
                                                        <Grid container alignItems="center" wrap="nowrap">
                                                            {type == "CAR" ? (
                                                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M14.45 7.13008H13.4167V17.5651H14.45V7.13008Z" fill="#18395B"/>
                                                                    <path d="M10.3166 7.13008H11.3499V17.5651H10.3166V7.13008Z" fill="#18395B"/>
                                                                    <path d="M8.25001 7.13008H7.21668V17.5651H8.25001V7.13008Z" fill="#18395B"/>
                                                                    <path d="M4.11623 7.13008H5.14956V17.5651H4.11623V7.13008Z" fill="#18395B"/>
                                                                    <path d="M6.18348 24.3484H5.15015V25.3919H6.18348V24.3484Z" fill="#18395B"/>
                                                                    <path d="M12.3836 24.3484H13.417V25.3919H12.3836V24.3484Z" fill="#18395B"/>
                                                                    <path d="M25.3 24.3484H24.2667V25.3919H25.3V24.3484Z" fill="#18395B"/>
                                                                    <path d="M23.2335 18.0871H21.1668V19.1306H23.2335V18.0871Z" fill="#18395B"/>
                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M28.6309 11.0822L31.1435 15.778C31.3778 16.1783 31.501 16.6351 31.5 17.1001V23.8265C31.5 24.2416 31.3367 24.6398 31.046 24.9333C30.7553 25.2268 30.3611 25.3918 29.95 25.3918H27.8368C27.7154 26.1209 27.342 26.783 26.783 27.2605C26.224 27.738 25.5155 28 24.7833 28C24.0512 28 23.3427 27.738 22.7836 27.2605C22.2246 26.783 21.8513 26.1209 21.7298 25.3918H15.9535C15.8321 26.1209 15.4587 26.783 14.8997 27.2605C14.3407 27.738 13.6322 28 12.9 28C12.1678 28 11.4593 27.738 10.9003 27.2605C10.3413 26.783 9.96793 26.1209 9.8465 25.3918H8.72017C8.59873 26.1209 8.22536 26.783 7.66635 27.2605C7.10733 27.738 6.39884 28 5.66667 28C4.93449 28 4.226 27.738 3.66698 27.2605C3.10797 26.783 2.7346 26.1209 2.61317 25.3918H1.53333C1.25928 25.3918 0.996444 25.2818 0.802656 25.0861C0.608869 24.8904 0.5 24.625 0.5 24.3483V5.0435C0.5 4.76675 0.608869 4.50133 0.802656 4.30563C0.996444 4.10994 1.25928 4 1.53333 4H17.0333C17.3074 4 17.5702 4.10994 17.764 4.30563C17.9578 4.50133 18.0667 4.76675 18.0667 5.0435V21.2178H19.1V10.7828C19.1 10.6444 19.1544 10.5117 19.2513 10.4138C19.3482 10.316 19.4796 10.261 19.6167 10.261H27.2669C27.5465 10.2609 27.8209 10.3372 28.061 10.4818C28.3011 10.6264 28.4981 10.8339 28.6309 11.0822ZM30.0879 16.0003L27.7216 11.5784C27.6773 11.4956 27.6117 11.4264 27.5317 11.3782C27.4516 11.33 27.3602 11.3045 27.2669 11.3045H22.2V16.0003H30.0879ZM19.1 24.3483V22.2613H18.0667V24.3483H19.1ZM17.0333 18.609V5.0435H1.53333V19.6525H13.7194L14.6014 18.7619C14.6983 18.664 14.8296 18.609 14.9667 18.609H17.0333ZM4.51849 26.6053C4.85835 26.8346 5.25792 26.957 5.66667 26.957C6.21478 26.957 6.74044 26.7371 7.12802 26.3457C7.51559 25.9543 7.73333 25.4235 7.73333 24.87C7.73333 24.4572 7.61212 24.0537 7.38504 23.7105C7.15795 23.3673 6.83518 23.0998 6.45755 22.9419C6.07991 22.7839 5.66437 22.7426 5.26348 22.8231C4.86259 22.9036 4.49434 23.1024 4.20531 23.3943C3.91628 23.6861 3.71945 24.058 3.63971 24.4628C3.55997 24.8677 3.60089 25.2873 3.75731 25.6687C3.91374 26.05 4.17863 26.376 4.51849 26.6053ZM11.7518 26.6053C12.0917 26.8346 12.4912 26.957 12.9 26.957C13.4481 26.957 13.9738 26.7371 14.3614 26.3457C14.7489 25.9543 14.9667 25.4235 14.9667 24.87C14.9667 24.4572 14.8455 24.0537 14.6184 23.7105C14.3913 23.3673 14.0685 23.0998 13.6909 22.9419C13.3132 22.7839 12.8977 22.7426 12.4968 22.8231C12.0959 22.9036 11.7277 23.1024 11.4386 23.3943C11.1496 23.6861 10.9528 24.058 10.873 24.4628C10.7933 24.8677 10.8342 25.2873 10.9906 25.6687C11.1471 26.05 11.412 26.376 11.7518 26.6053ZM10.9015 22.4808C11.4603 22.0034 12.1681 21.7409 12.9 21.7395C13.6319 21.7409 14.3397 22.0034 14.8985 22.4808C15.4572 22.9582 15.8309 23.6196 15.9535 24.3483H17.0333V19.6525H15.1806L14.2986 20.5431C14.2017 20.641 14.0703 20.696 13.9333 20.696H1.53333V24.3483H2.61317C2.7346 23.6191 3.10797 22.957 3.66698 22.4795C4.226 22.002 4.93449 21.74 5.66667 21.74C6.39884 21.74 7.10733 22.002 7.66635 22.4795C8.22536 22.957 8.59873 23.6191 8.72017 24.3483H9.8465C9.96909 23.6196 10.3428 22.9582 10.9015 22.4808ZM23.6351 26.6053C23.975 26.8346 24.3746 26.957 24.7833 26.957C25.3314 26.957 25.8571 26.7371 26.2447 26.3457C26.6323 25.9543 26.85 25.4235 26.85 24.87C26.85 24.4572 26.7288 24.0537 26.5017 23.7105C26.2746 23.3673 25.9518 23.0998 25.5742 22.9419C25.1966 22.7839 24.781 22.7426 24.3801 22.8231C23.9792 22.9036 23.611 23.1024 23.322 23.3943C23.0329 23.6861 22.8361 24.058 22.7564 24.4628C22.6766 24.8677 22.7176 25.2873 22.874 25.6687C23.0304 26.05 23.2953 26.376 23.6351 26.6053ZM27.8368 24.3483H29.95C30.087 24.3483 30.2184 24.2933 30.3153 24.1954C30.4122 24.0976 30.4667 23.9649 30.4667 23.8265V23.3048H28.9167V22.2613H30.4667V21.2178H28.4C28.1259 21.2178 27.8631 21.1078 27.6693 20.9121C27.4755 20.7164 27.3667 20.451 27.3667 20.1743V19.1308C27.3667 18.854 27.4755 18.5886 27.6693 18.3929C27.8631 18.1972 28.1259 18.0873 28.4 18.0873H30.4667V17.1001C30.4667 17.0904 30.4647 17.0809 30.4628 17.0716C30.4608 17.0623 30.4589 17.0531 30.4589 17.0438H21.6833C21.5463 17.0438 21.4149 16.9888 21.318 16.8909C21.2211 16.7931 21.1667 16.6604 21.1667 16.522V11.3045H20.1333V24.3483H21.7298C21.8513 23.6191 22.2246 22.957 22.7836 22.4795C23.3427 22.002 24.0512 21.74 24.7833 21.74C25.5155 21.74 26.224 22.002 26.783 22.4795C27.342 22.957 27.7154 23.6191 27.8368 24.3483ZM28.4 20.1743H30.4667V19.1308H28.4V20.1743Z" fill="#18395B"/>
                                                                </svg>
                                                            ) : (
                                                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M20.9072 16.8198C21.2151 16.9965 21.567 17.0901 21.9254 17.0901C22.6785 17.0901 23.3723 16.6767 23.7364 16.0121C24.2939 14.9982 23.9383 13.7087 22.9418 13.1356L18.1158 10.3844C17.9969 10.0036 17.7914 9.65302 17.508 9.35591C18.7118 8.69418 19.5315 7.39902 19.5315 5.91191C19.5315 3.75498 17.8067 2 15.6865 2C13.5663 2 11.8418 3.75498 11.8418 5.91191C11.8418 7.40276 12.6662 8.7004 13.8749 9.36058C13.8727 9.36276 13.8706 9.36524 13.8684 9.36742L11.3046 10.1188C10.157 10.2874 9.07402 11.2459 8.58968 12.1189L7.26632 14.5011C7.09204 14.8137 7 15.1715 7 15.5361C7 16.2993 7.40392 17.0052 8.05367 17.3785C8.37564 17.5636 8.72544 17.6514 9.07126 17.6514C9.79532 17.6514 10.4995 17.2659 10.8802 16.5802L12.0724 14.437L13.1734 13.9765L13.1425 17.932C13.1425 18.6416 13.3599 19.3255 13.7547 19.8932L8.91379 25.8003C8.19188 26.6817 8.30012 28.007 9.15596 28.7546C9.53175 29.0832 10.0115 29.2639 10.5065 29.2639C11.1248 29.2639 11.7064 28.9867 12.102 28.5039L16.5237 23.1024L17.0527 23.7096L19.5673 28.8315C19.9214 29.5523 20.6323 30 21.4227 30C21.7517 30 22.066 29.9241 22.3529 29.776C22.8506 29.5237 23.2212 29.0894 23.3964 28.5524C23.571 28.017 23.5301 27.4445 23.2815 26.9433L20.554 21.3962C20.5302 21.3477 20.4999 21.3026 20.4647 21.2618L18.4274 18.921C18.3063 18.7826 18.2366 18.5956 18.2366 18.408V15.3233L20.9072 16.8198ZM22.6684 15.4055C22.4486 15.8071 21.9061 15.9642 21.5025 15.7331L18.2363 13.9028V11.88L22.3418 14.2201C22.7519 14.456 22.8986 14.987 22.6684 15.4055ZM13.0649 5.91191C13.0649 4.44129 14.2412 3.24444 15.6865 3.24444C17.1319 3.24444 18.3082 4.44129 18.3082 5.91191C18.3082 7.38253 17.1319 8.57938 15.6865 8.57938C14.2412 8.57938 13.0649 7.38284 13.0649 5.91191ZM11.1621 27.7071C10.8698 28.064 10.3004 28.1144 9.95279 27.8104C9.60146 27.5036 9.55682 26.9592 9.85342 26.5977L14.5616 20.8521L15.7024 22.1603L11.1621 27.7071ZM19.4961 22.0262L22.1887 27.5024C22.2905 27.7077 22.3073 27.9417 22.2357 28.1604C22.1639 28.3804 22.0119 28.5586 21.804 28.6641C21.6863 28.7248 21.5582 28.7556 21.4227 28.7556C21.0986 28.7556 20.8066 28.5714 20.6614 28.2752L18.1109 23.08C18.0871 23.0314 18.0568 22.9857 18.0214 22.9449L16.9668 21.7344C16.9662 21.7338 16.9655 21.7335 16.9652 21.7328L15.0047 19.4848C15.0031 19.4829 15.001 19.482 14.9995 19.4801L14.8958 19.36C14.554 18.9686 14.3656 18.4615 14.3656 17.9367L14.4038 13.0696C14.4075 12.9825 14.3944 12.8936 14.3598 12.8077C14.2632 12.5688 14.0372 12.4238 13.7978 12.4222C13.7978 12.4222 13.7975 12.4222 13.7972 12.4222C13.7957 12.4222 13.7938 12.4222 13.7923 12.4222C13.7755 12.4222 13.7593 12.426 13.7428 12.4272C13.7208 12.4291 13.6987 12.4294 13.677 12.434C13.6321 12.4428 13.5893 12.4564 13.5483 12.4745L11.4119 13.368C11.2853 13.4209 11.1798 13.5158 11.1126 13.6368L9.81581 15.9673C9.58495 16.3836 9.06331 16.5298 8.65542 16.2952C8.38879 16.1419 8.22307 15.851 8.22307 15.5364C8.22307 15.3868 8.26006 15.2412 8.3307 15.1149L9.65467 12.7315C10.0332 12.0492 10.8637 11.4285 11.5061 11.3473C11.5379 11.3433 11.5694 11.3371 11.6003 11.3277L14.3705 10.516C14.3904 10.5101 14.4081 10.5008 14.4271 10.493C14.4424 10.4868 14.458 10.4818 14.4726 10.4744C14.5023 10.4594 14.5295 10.4414 14.5558 10.4221C14.5638 10.4162 14.5729 10.4118 14.5806 10.4056C14.6145 10.3782 14.6454 10.3477 14.6726 10.3141C14.6732 10.3132 14.6744 10.3126 14.675 10.3116C14.923 10.0024 15.293 9.82507 15.6902 9.82507C16.0431 9.82507 16.3769 9.96538 16.6258 10.2161C16.8763 10.4734 17.0145 10.8132 17.0145 11.1722V18.4086C17.0145 18.9045 17.1921 19.3805 17.5138 19.7483L19.4961 22.0262Z" fill="#18395B"/>
                                                                </svg>
                                                            )}

                                                            <Typography color="primary" variant="screenText" display="block" width="100%" marginLeft="16px">
                                                                {type == "CAR" ? "Автомобильный" : "Пешеходный"}
                                                            </Typography>
                                                            <Checkbox checked={types.includes(type)}/>
                                                        </Grid>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        )}
                        {step == "company" && (
                            <>
                                <Grid item>
                                    <Typography variant="screenText" color="primary">
                                        Компания
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <CompanyFormCard
                                        isCompanyDataLoading={companyDataLoading}
                                        setCompanyDataLoading={setCompanyDataLoading}
                                    />
                                </Grid>
                            </>
                        )}
                        {step == "employee" && (
                            <>
                                <Grid item>
                                    <Typography variant="screenText" color="primary">
                                        Личные данные
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <PersonalFormCard/>
                                </Grid>
                                <Grid item>
                                    <Typography variant="screenText" color="primary">
                                        Паспортные данные
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <PassportFormCard types={types}/>
                                </Grid>
                            </>
                        )}
                        {step == "car" && (
                            <>
                                <Grid item>
                                    <Typography variant="screenText" color="primary">
                                        Автомобиль
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <CarFormCard/>
                                </Grid>
                            </>
                        )}
                        {step == "admission" && (
                            <>
                                <Grid item>
                                    <Typography variant="screenText" color="primary">
                                        Цель и зоны посещения
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <AdmissionFormCard types={types}/>
                                </Grid>
                                <Grid item>
                                    <Typography variant="screenText" color="primary">
                                        Правила нахождения
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <AgreementFormCard types={types}/>
                                </Grid>
                            </>
                        )}
                        {
                            step == "upload" && (
                                <>
                                    <Grid item>
                                        <Typography variant="screenText" color="primary">
                                            Документы
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <UploadFormCard loading={loading} />
                                    </Grid>
                                </>
                            )
                        }
                    </Grid>

                    <Box height="80px"/>
                    <FloatingActions>
                        <Button
                            type="submit"
                            fullWidth
                            color="primary"
                            disabled={submitDisabled}
                        >
                            {isSubmitting || loading
                                ? <LoaderLight className={classes.loader} />
                                : step == "upload"
                                    ? "Создать заявку"
                                    : "Далее"
                            }
                        </Button>
                    </FloatingActions>
                </FormProvider>
            </form>
            <PopupMessage
                onClose={() => setAlertVisible({open: false, notification: null})}
                open={isAlertVisible.open}
                notification={isAlertVisible.notification}
                autoHideDuration={5000}
            />
        </Box>
    )
}
